// psyMeta.ts
// import type { PsyMeta, TargetType } from './types';

export type TargetType = '자녀용' | '부모용' | '부모보고용';

export interface PsyMeta {
    target: TargetType;
    englishTitle: string;
    koreanTitle: string;
}

// 1) 변형을 캐논키로 정규화
export function normalizePsyId(raw?: string): string {
    if (!raw) return '';
    let s = raw.trim().toUpperCase();

    // 공백/하이픈/밑줄/중복 제거
    s = s.replace(/\s+/g, '');
    // KICS-L 변형 → KICSL
    s = s.replace(/^KICS-?L$/, 'KICSL');
    // 흔한 접미사( _CO_SG, _CO_TG, _CO_PG, _EX_* )는 남겨도 되지만,
    // 매핑의 기준을 "주요 축약어"로 통일하면 안정적.
    // 다만 필요 항목은 조건적으로 유지:
    // 예: KBASC3_EX_PG_C / KBASC3_EX_PG_P 는 부모보고용으로 한 묶음 취급.
    return s;
}

// 2) 캐논키를 만든 후, 세부 패턴에 맞춰 최종 키 생성
function canonicalKey(id: string): string {
    const s = normalizePsyId(id);

    // KBASC3 변형들
    if (s.startsWith('KBASC3_EX_PG_')) return 'KBASC3_EX_PG'; // 부모보고용
    if (s.startsWith('KBASC3_EX_SG_')) return 'KBASC3_EX_SG'; // 자녀용

    // KCDI2
    if (s.startsWith('KCDI2_CO_SG')) return 'KCDI2_CO_SG'; // 자녀용
    if (s.startsWith('KCDI2_CO_PG')) return 'KCDI2_CO_PG'; // 부모보고용

    // MindFitR
    if (s.startsWith('MINDFITR_CO_SG')) return 'MINDFITR_CO_SG_E';

    // RS
    if (s.startsWith('RS_CO_SG')) return 'RS_CO_SG_CA';

    // IBS, KTABS, PAT, KRCMAS2
    if (s.startsWith('IBS_CO_SG')) return 'IBS_CO_SG';
    if (s.startsWith('KTABS_CO_TG')) return 'KTABS_CO_TG';
    if (s.startsWith('PAT_CO_CA')) return 'PAT_CO_CA';
    if (s.startsWith('KRCMAS2_CO_SG_A')) return 'KRCMAS2_CO_SG_A';

    // KICS / KICS-L
    if (s === 'KICSL' || s.startsWith('KICSL_CO_SG')) return 'KICSL_CO_SG';
    if (s.startsWith('KICS_CO_SG')) return 'KICS_CO_SG';

    // 기본 반환
    return s;
}

const META: Record<string, PsyMeta> = {
    // 부모용
    PAT_CO_CA: { target: '부모용', englishTitle: 'PAT-2', koreanTitle: '부모 양육태도 검사' },

    // 부모보고용
    IBS_CO_SG: { target: '부모보고용', englishTitle: 'IBS', koreanTitle: '부모아동 상호작용 검사' },
    KTABS_CO_TG: { target: '부모보고용', englishTitle: 'K-TABS', koreanTitle: '영유아 기질 및 비전형 행동척도' },
    KCDI2_CO_PG: { target: '부모보고용', englishTitle: 'K-CDI 2', koreanTitle: '아동 우울 척도' },
    KBASC3_EX_PG: { target: '부모보고용', englishTitle: 'K-BASC-3', koreanTitle: '정서-행동 평가시스템' },

    // 자녀용
    KCDI2_CO_SG: { target: '자녀용', englishTitle: 'K-CDI 2', koreanTitle: '아동 우울 척도' },
    MINDFITR_CO_SG_E: { target: '자녀용', englishTitle: 'MindFit', koreanTitle: '적응역량 검사' },
    RS_CO_SG_CA: { target: '자녀용', englishTitle: 'RS', koreanTitle: '회복탄력성 검사' },
    KRCMAS2_CO_SG_A: { target: '자녀용', englishTitle: 'RCMAS-2', koreanTitle: '아동 불안 척도' },
    KBASC3_EX_SG: { target: '자녀용', englishTitle: 'K-BASC-3', koreanTitle: '정서-행동 평가시스템' },
    KICSL_CO_SG: { target: '자녀용', englishTitle: 'KICS-L', koreanTitle: '아동 진로성격 강점 검사' },
    KICS_CO_SG: { target: '자녀용', englishTitle: 'KICS', koreanTitle: '아동 진로성격 강점 검사' },
};

export function resolvePsyMeta(rawId: string): PsyMeta | null {
    const key = canonicalKey(rawId);
    return META[key] ?? null;
}
